import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { toast } from "react-toastify";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminViewAction from "./SuperAdminViewAction";
import {
  formatApiDate,
  formatDisplayDateTime,
  getErrorLogById,
  getErrorLogStats,
  getErrorLogs,
} from "../../api/superAdminApi";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const FILTER_ITEM = { xs: 12, sm: 6, md: 4, lg: 3 };
const MESSAGE_TRUNCATE = 80;

const SEVERITY_OPTIONS = [
  { value: "", label: "All" },
  { value: "critical", label: "Critical" },
  { value: "error", label: "Error" },
];

const filterFormControlSx = { minWidth: { xs: "100%", sm: 220 }, width: "100%" };
const filterMenuProps = {
  PaperProps: { sx: { maxHeight: 320, minWidth: 240, mt: 0.5 } },
};

const TABLE_HEADERS = [
  "S.No",
  "Created at",
  "Severity",
  "Source",
  "Code",
  "Message",
  "Location",
  "Action",
];

function FilterSectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

function FilterSelect({ label, labelId, value, onChange, options }) {
  return (
    <FormControl fullWidth size="small" sx={filterFormControlSx}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        displayEmpty
        notched
        MenuProps={filterMenuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value || "all"} value={o.value}>
            {o.value ? o.label : <em>{o.label}</em>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function StatCard({ label, value, color, sublabel }) {
  return (
    <Card elevation={0} sx={{ bgcolor: color, color: "#fff", borderRadius: 2, height: "100%" }}>
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="body2" sx={{ opacity: 0.92, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value ?? "—"}
        </Typography>
        {sublabel ? (
          <Typography variant="caption" sx={{ opacity: 0.85, display: "block", mt: 0.5 }}>
            {sublabel}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SeverityChip({ severity }) {
  const normalized = String(severity || "").toLowerCase();
  if (normalized === "critical") {
    return <Chip label="Critical" size="small" color="error" sx={{ fontWeight: 600 }} />;
  }
  return <Chip label="Error" size="small" color="warning" sx={{ fontWeight: 600 }} />;
}

function truncateMessage(message, max = MESSAGE_TRUNCATE) {
  const text = message || "";
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function fileBasename(filePath) {
  if (!filePath) return "—";
  const parts = String(filePath).split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}

function formatLocation(row) {
  const file = fileBasename(row?.file_name);
  if (file === "—") return "—";
  const line = row?.line_number != null ? `:${row.line_number}` : "";
  return `${file}${line}`;
}

async function copyText(text, label = "Copied") {
  if (!text) {
    toast.info("Nothing to copy.");
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard.`);
  } catch {
    toast.error("Copy failed.");
  }
}

function JsonBlock({ title, data, onCopy }) {
  if (data == null || (typeof data === "object" && Object.keys(data).length === 0)) {
    return null;
  }
  const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => onCopy(text, title)}>
          Copy
        </Button>
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: "grey.50",
          maxHeight: 220,
          overflow: "auto",
          fontFamily: "monospace",
          fontSize: "0.75rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Paper>
    </Box>
  );
}

function DetailField({ label, value, mono = false }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          wordBreak: "break-word",
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: mono ? "0.8rem" : "inherit",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Grid>
  );
}

function ErrorLogDetailDialog({ open, errorId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !errorId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getErrorLogById(errorId);
        if (cancelled) return;
        const body = res.data;
        if (body?.status_code === 200) {
          setDetail(body.data);
        } else {
          setDetail(null);
          toast.error(body?.message || "Failed to load error detail.");
        }
      } catch (err) {
        if (!cancelled) {
          setDetail(null);
          toast.error(err?.response?.data?.message || "Failed to load error detail.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, errorId]);

  const handleClose = () => onClose();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        Error log #{errorId}
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !detail ? (
          <Typography color="text.secondary">Error log not found.</Typography>
        ) : (
          <Stack spacing={2.5}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <SeverityChip severity={detail.severity} />
                {detail.error_code ? (
                  <Chip label={detail.error_code} size="small" variant="outlined" />
                ) : null}
                {detail.source ? (
                  <Chip label={detail.source} size="small" sx={{ fontFamily: "monospace" }} />
                ) : null}
              </Stack>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                  {detail.error_message}
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyText(detail.error_message, "Error message")}
                >
                  Copy
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Logged at {formatDisplayDateTime(detail.created_at)}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Location
              </Typography>
              <Grid container spacing={2}>
                <DetailField label="File" value={detail.file_name} mono />
                <DetailField
                  label="Line : Column"
                  value={
                    detail.line_number != null
                      ? `${detail.line_number}${detail.column_number != null ? ` : ${detail.column_number}` : ""}`
                      : "—"
                  }
                />
                <DetailField label="Error name" value={detail.error_name} />
              </Grid>
            </Box>

            {(detail.request_method ||
              detail.request_url ||
              detail.request_ip ||
              detail.user_id ||
              detail.status_code != null) && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Request
                  </Typography>
                  <Grid container spacing={2}>
                    <DetailField label="Method" value={detail.request_method} />
                    <DetailField label="URL" value={detail.request_url} mono />
                    <DetailField label="IP" value={detail.request_ip} />
                    <DetailField label="User ID" value={detail.user_id} mono />
                    <DetailField label="HTTP status" value={detail.status_code} />
                  </Grid>
                </Box>
              </>
            )}

            <JsonBlock title="Context" data={detail.context} onCopy={copyText} />
            <JsonBlock title="Inner error" data={detail.inner_error} onCopy={copyText} />

            {detail.stack_trace ? (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Stack trace
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyText(detail.stack_trace, "Stack trace")}
                  >
                    Copy
                  </Button>
                </Stack>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: "grey.900",
                    color: "grey.100",
                    maxHeight: 280,
                    overflow: "auto",
                    fontFamily: "monospace",
                    fontSize: "0.72rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {detail.stack_trace}
                </Paper>
              </Box>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function VMSuperAdminErrorLogs() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [severity, setSeverity] = useState("");
  const [source, setSource] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const requestSeq = useRef(0);

  const buildFilterParams = useCallback(
    (overrides = {}) => {
      const params = {};
      const from = formatApiDate(overrides.fromDate !== undefined ? overrides.fromDate : fromDate);
      const to = formatApiDate(overrides.toDate !== undefined ? overrides.toDate : toDate);
      const nextSeverity = overrides.severity !== undefined ? overrides.severity : severity;
      const nextSource = overrides.source !== undefined ? overrides.source : source;
      const nextErrorCode = overrides.errorCode !== undefined ? overrides.errorCode : errorCode;
      const nextSearch =
        overrides.appliedSearch !== undefined ? overrides.appliedSearch : appliedSearch;

      if (from) params.from = from;
      if (to) params.to = to;
      if (nextSeverity) params.severity = nextSeverity;
      if (nextSource.trim()) params.source = nextSource.trim();
      if (nextErrorCode.trim()) params.error_code = nextErrorCode.trim();
      if (nextSearch.trim()) params.search = nextSearch.trim();
      return params;
    },
    [fromDate, toDate, severity, source, errorCode, appliedSearch]
  );

  const loadStats = useCallback(
    async (filterOverrides = {}) => {
      setStatsLoading(true);
      try {
        const res = await getErrorLogStats(buildFilterParams(filterOverrides));
        const body = res.data;
        if (body?.status_code === 200) {
          setStats(body.data ?? null);
        } else {
          setStats(null);
        }
      } catch {
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    },
    [buildFilterParams]
  );

  const loadList = useCallback(
    async (pageOverride, filterOverrides = {}) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      try {
        const res = await getErrorLogs({
          ...buildFilterParams(filterOverrides),
          page: pageOverride !== undefined ? pageOverride : page,
          no_of_rows: rowsPerPage,
        });
        if (seq !== requestSeq.current) return;
        const body = res.data;
        if (body?.status_code === 200) {
          setRows(Array.isArray(body.data) ? body.data : []);
          setTotalRecords(body.totalCount ?? 0);
        } else {
          setRows([]);
          setTotalRecords(0);
          toast.error(body?.message || "Failed to load error logs.");
        }
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setRows([]);
        setTotalRecords(0);
        toast.error(err?.response?.data?.message || "Error logs API unavailable.");
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [buildFilterParams, page, rowsPerPage]
  );

  const loadAll = useCallback(
    async (pageOverride = 0, filterOverrides = {}) => {
      await Promise.all([loadStats(filterOverrides), loadList(pageOverride, filterOverrides)]);
    },
    [loadStats, loadList]
  );

  useEffect(() => {
    loadAll(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    const overrides = {
      fromDate,
      toDate,
      severity,
      source,
      errorCode,
      appliedSearch: nextSearch,
    };
    setAppliedSearch(nextSearch);
    setPage(0);
    setFiltersOpen(false);
    loadAll(0, overrides);
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSeverity("");
    setSource("");
    setErrorCode("");
    setSearchInput("");
    setAppliedSearch("");
    setPage(0);
    const emptyOverrides = {
      fromDate: null,
      toDate: null,
      severity: "",
      source: "",
      errorCode: "",
      appliedSearch: "",
    };
    loadAll(0, emptyOverrides);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    loadList(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    loadList(0);
  };

  const summary = stats?.summary ?? {};
  const topSources = Array.isArray(stats?.top_sources) ? stats.top_sources.slice(0, 3) : [];

  return (
    <Box>
      <SuperAdminPageHeader
        title="Error Logs"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={() => loadAll(page)}
              disabled={loading || statsLoading}
            >
              Refresh
            </Button>
            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Filters
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total errors"
            value={statsLoading ? "…" : summary.total ?? 0}
            color="#1565c0"
            sublabel={summary.last_7d != null ? `Last 7 days: ${summary.last_7d}` : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Last 24 hours"
            value={statsLoading ? "…" : summary.last_24h ?? 0}
            color="#6a1b9a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Critical"
            value={statsLoading ? "…" : summary.critical_count ?? 0}
            color="#c62828"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Standard errors"
            value={statsLoading ? "…" : summary.error_count ?? 0}
            color="#ef6c00"
          />
        </Grid>
      </Grid>

      {topSources.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
            Top sources
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {topSources.map((item) => (
              <Chip
                key={item.source}
                label={`${item.source} (${item.count})`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: "monospace", mb: 0.5 }}
              />
            ))}
          </Stack>
        </Paper>
      ) : null}

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2.5}>
            <FilterSectionTitle>Date range</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="From"
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="To"
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Error filters</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  label="Severity"
                  labelId="error-log-severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  options={SEVERITY_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <TextField
                  fullWidth
                  size="small"
                  label="Source"
                  placeholder="e.g. emailService.sendTemplate"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <TextField
                  fullWidth
                  size="small"
                  label="Error code"
                  placeholder="e.g. EAUTH"
                  value={errorCode}
                  onChange={(e) => setErrorCode(e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Search</FilterSectionTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Message, source, file, code, URL"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={handleSearch} disabled={loading || statsLoading} fullWidth>
                    {loading ? <CircularProgress size={22} color="inherit" /> : "Apply"}
                  </Button>
                  <Button variant="outlined" onClick={handleReset} disabled={loading || statsLoading}>
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {TABLE_HEADERS.map((h) => (
                <TableCell
                  key={h}
                  align={h === "Action" ? "center" : "left"}
                  sx={{ color: "primary.contrastText", fontWeight: 700 }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No error logs for the current criteria.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const fullMessage = row.error_message || "";
                const shortMessage = truncateMessage(fullMessage);
                return (
                  <TableRow key={row.id ?? idx} hover>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDisplayDateTime(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <SeverityChip severity={row.severity} />
                    </TableCell>
                    <TableCell>
                      {row.source ? (
                        <Chip label={row.source} size="small" sx={{ fontFamily: "monospace", maxWidth: 180 }} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{row.error_code || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Tooltip title={fullMessage || ""} placement="top-start">
                        <Typography variant="body2" noWrap>
                          {shortMessage || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", maxWidth: 160 }}>
                      <Typography variant="body2" noWrap title={row.file_name}>
                        {formatLocation(row)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <SuperAdminViewAction onClick={() => setDetailId(row.id)} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                disabled={loading}
                colSpan={TABLE_HEADERS.length}
                sx={{ border: 0 }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <ErrorLogDetailDialog
        open={Boolean(detailId)}
        errorId={detailId}
        onClose={() => setDetailId(null)}
      />
    </Box>
  );
}
